import React, { createContext, useContext, useState, useEffect } from 'react';

const FavoritesContext = createContext({ favorites: [], toggle: () => {}, isSaved: () => false });

/**
 * FavoritesProvider — localStorage-backed favorites for voucher offers.
 *
 * Stores a flat array of offer snapshots under 'pricemap_favorites'.
 * No backend needed — everything renders from the saved snapshot.
 *
 * Snapshot mirrors the shape VoucherCard reads so the favorites page
 * can render the exact same card UI as home / discounts. Legacy Club
 * fields (partner_slug, headline_ka, pitch_ka, etc.) are kept as
 * passthroughs for older saved items.
 */
export function FavoritesProvider({ children }) {
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem('pricemap_favorites');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('pricemap_favorites', JSON.stringify(favorites));
  }, [favorites]);

  const toggle = (offer) => {
    setFavorites((prev) => {
      const id = offer.product_id || offer._id;
      const exists = prev.some((f) => (f.product_id || f._id) === id);
      if (exists) return prev.filter((f) => (f.product_id || f._id) !== id);
      const snapshot = {
        _id:                  offer._id || id,
        product_id:           id,
        partner_id:           offer.partner_id,
        partner_name:         offer.partner_name,
        partner_category:     offer.partner_category,
        partner_main_photo:   offer.partner_main_photo,
        partner_logo_url:     offer.partner_logo_url,
        partner_photos:       offer.partner_photos,
        title:                offer.title,
        description:          offer.description,
        discount_percent:     offer.discount_percent,
        is_multi_use:         offer.is_multi_use,
        cooldown_hours:       offer.cooldown_hours,
        distance_km:          offer.distance_km,
        // Legacy Club fields kept for backward compat with old snapshots
        partner_slug:         offer.partner_slug,
        headline_ka:          offer.headline_ka,
        pitch_ka:             offer.pitch_ka,
        name_ka:              offer.name_ka,
        name:                 offer.name,
        images:               offer.images,
        image_url:            offer.image_url,
        partner_logo:         offer.partner_logo,
        valid_until:          offer.valid_until,
        savedAt:              Date.now(),
      };
      return [snapshot, ...prev];
    });
  };

  const isSaved = (id) => favorites.some((f) => (f.product_id || f._id) === id);

  return (
    <FavoritesContext.Provider value={{ favorites, toggle, isSaved }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
